<div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-xl">
  <div>
    <h1 class="font-headline-lg text-headline-lg text-text-main dark:text-on-surface mb-1">Jobs &amp; Dispatch</h1>
    <p class="text-text-muted dark:text-on-surface-variant font-body-sm text-body-sm">Status is read-only here once a job is dispatched - technicians update it from their own job queue.</p>
    @if (error()) {
      <p class="text-error font-body-sm text-body-sm">{{ error() }}</p>
    }
  </div>
  <div class="flex items-center gap-3">
    <button (click)="openDispatchModal()" class="h-[48px] px-6 bg-[#00B9F2] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 font-label-md">
      <span class="material-symbols-outlined">add_task</span>
      New Dispatch
    </button>
  </div>
</div>

<div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl flex flex-col overflow-hidden shadow-sm">
  <div class="p-md border-b border-border-subtle dark:border-outline-variant flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div class="flex flex-wrap items-center gap-3">
      <select [value]="statusFilter()" (change)="onStatusFilterChange($any($event.target).value)"
              class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] transition-all h-[48px] text-text-main dark:text-white">
        <option value="">All Statuses</option>
        @for (status of statusOptions; track status) {
          <option [value]="status">{{ status }}</option>
        }
      </select>

      <label class="relative">
        <input [value]="dealerFilterSearchTerm()" (input)="onDealerFilterSearchInput($any($event.target).value)"
               (focus)="onDealerFilterFocus()" (blur)="onDealerFilterBlur()"
               type="text" placeholder="Filter by dealer..."
               class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg pl-3 pr-9 py-2 text-body-sm focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] transition-all h-[48px] text-text-main dark:text-white w-56">
        @if (dealerFilterId()) {
          <button type="button" (click)="clearDealerFilter()" title="Clear dealer filter"
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted dark:text-on-surface-variant hover:text-[#ef4444]">
            <span class="material-symbols-outlined text-[18px]">close</span>
          </button>
        }
        @if (dealerFilterFieldFocused() && !dealerFilterId()) {
          <div class="absolute top-full left-0 right-0 mt-1 bg-background dark:bg-surface-container border border-border-subtle dark:border-outline-variant rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto w-56">
            @if (dealersLoading()) {
              <p class="px-3 py-2 text-body-sm text-text-muted dark:text-on-surface-variant">Loading dealers...</p>
            } @else if (dealerFilterSearchResults().length === 0) {
              <p class="px-3 py-2 text-body-sm text-text-muted dark:text-on-surface-variant">No matching dealers.</p>
            } @else {
              @for (dealer of dealerFilterSearchResults(); track dealer.dealerId) {
                <button type="button" (click)="selectDealerFilter(dealer)"
                        class="w-full text-left px-3 py-2 text-body-sm text-text-main dark:text-on-surface hover:bg-surface-variant dark:hover:bg-surface-variant transition-colors truncate">
                  {{ dealer.dealerName }}
                </button>
              }
            }
          </div>
        }
      </label>
    </div>
    <div class="flex items-center gap-3 text-text-muted dark:text-on-surface-variant font-label-sm">
      <span>Page {{ page() }} of {{ totalPages() }} &middot; {{ totalCount() }} jobs</span>
      <div class="flex gap-1">
        <button (click)="goToPreviousPage()" [disabled]="page() <= 1" class="w-8 h-8 rounded hover:bg-surface-variant dark:hover:bg-surface-variant flex items-center justify-center disabled:opacity-50">
          <span class="material-symbols-outlined text-[20px]">chevron_left</span>
        </button>
        <button (click)="goToNextPage()" [disabled]="page() >= totalPages()" class="w-8 h-8 rounded hover:bg-surface-variant dark:hover:bg-surface-variant flex items-center justify-center disabled:opacity-50">
          <span class="material-symbols-outlined text-[20px]">chevron_right</span>
        </button>
      </div>
    </div>
  </div>

  <div class="overflow-x-auto">
    <table class="w-full text-left border-collapse min-w-[1000px]">
      <thead>
        <tr class="bg-surface dark:bg-surface-container border-b border-border-subtle dark:border-outline-variant">
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium">Dealer</th>
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium">Technician</th>
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium">Vehicle</th>
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium">Status</th>
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium">Scheduled</th>
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium">Total</th>
          <th class="p-4 font-label-md text-text-muted dark:text-on-surface-variant font-medium text-right">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-border-subtle dark:divide-outline-variant">
        @if (loading()) {
          <tr>
            <td colspan="7" class="p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm">Loading jobs...</td>
          </tr>
        } @else if (jobs().length === 0) {
          <tr>
            <td colspan="7" class="p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm">No jobs found.</td>
          </tr>
        }
        @for (job of jobs(); track job.jobId) {
          <tr class="hover:bg-surface dark:hover:bg-surface-variant/50 transition-colors group">
            <td class="p-4">
              <p class="font-label-md text-text-main dark:text-on-surface">{{ job.dealerName || 'N/A' }}</p>
              <p class="text-text-muted dark:text-on-surface-variant text-xs">Job #{{ job.jobId }}</p>
            </td>
            <td class="p-4">
              <p class="font-body-sm text-text-main dark:text-on-surface">{{ job.technicianName || 'Unassigned' }}</p>
            </td>
            <td class="p-4">
              <p class="font-body-sm text-text-main dark:text-on-surface">{{ job.vehicleName || 'N/A' }}</p>
            </td>
            <td class="p-4">
              <span class="inline-flex items-center px-2 py-1 rounded-full font-label-sm border"
                    [style.background-color]="statusColor(job.status) + '1A'"
                    [style.color]="statusColor(job.status)"
                    [style.border-color]="statusColor(job.status) + '33'">
                <span class="w-1.5 h-1.5 rounded-full mr-1.5" [style.background-color]="statusColor(job.status)"></span>
                {{ job.status }}
              </span>
            </td>
            <td class="p-4">
              <p class="font-body-sm text-text-main dark:text-on-surface">{{ job.scheduledDate ? (job.scheduledDate | date:'MMM d, y') : 'N/A' }}</p>
            </td>
            <td class="p-4">
              <p class="font-body-sm text-text-main dark:text-on-surface">{{ job.totalAmount != null ? ('$' + job.totalAmount.toFixed(2)) : 'N/A' }}</p>
            </td>
            <td class="p-4 text-right">
              <div class="flex items-center justify-end gap-2">
                @if (canMarkScheduled(job)) {
                  <button (click)="markScheduled(job)" title="Confirm Scheduled"
                          class="p-2 text-text-muted dark:text-on-surface-variant hover:text-[#14b8a6] rounded hover:bg-surface-variant dark:hover:bg-surface-container-highest transition-colors">
                    <span class="material-symbols-outlined text-[20px]">event_available</span>
                  </button>
                }
                @if (canPause(job)) {
                  <button (click)="pauseJob(job)" title="Pause job"
                          class="p-2 text-text-muted dark:text-on-surface-variant hover:text-[#F29727] rounded hover:bg-surface-variant dark:hover:bg-surface-container-highest transition-colors">
                    <span class="material-symbols-outlined text-[20px]">pause_circle</span>
                  </button>
                }
                @if (canEdit(job)) {
                  <button (click)="openEditJobModal(job)" title="Edit job"
                          class="p-2 text-text-muted dark:text-on-surface-variant hover:text-[#00B9F2] rounded hover:bg-surface-variant dark:hover:bg-surface-container-highest transition-colors">
                    <span class="material-symbols-outlined text-[20px]">edit</span>
                  </button>
                } @else {
                  <button (click)="openHistoryModal(job)" title="View history"
                          class="p-2 text-text-muted dark:text-on-surface-variant hover:text-[#00B9F2] rounded hover:bg-surface-variant dark:hover:bg-surface-container-highest transition-colors">
                    <span class="material-symbols-outlined text-[20px]">history</span>
                  </button>
                }
              </div>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
</div>

@if (modalOpen()) {
  <div class="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" (click)="closeModal()">
    <div class="bg-background dark:bg-surface-container-low rounded-xl shadow-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto overflow-x-hidden" (click)="$event.stopPropagation()">
      <div class="p-lg border-b border-border-subtle dark:border-outline-variant flex items-center justify-between">
        <h2 class="font-headline-sm text-headline-sm text-text-main dark:text-on-surface">
          {{ editingJob() === null ? 'New Dispatch' : 'Edit Job #' + editingJob()!.jobId }}
        </h2>
        <button (click)="closeModal()" class="p-1 rounded-full hover:bg-surface-variant dark:hover:bg-surface-variant text-text-muted dark:text-on-surface-variant">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div class="p-lg flex flex-col gap-4">
        @if (editingJob(); as job) {
          <div class="border border-border-subtle dark:border-outline-variant rounded-lg p-4 flex items-center gap-3">
            <span class="material-symbols-outlined text-[#00B9F2]">store</span>
            <div>
              <p class="font-label-md text-text-main dark:text-on-surface">{{ job.dealerName }}</p>
              <p class="text-text-muted dark:text-on-surface-variant text-xs">Dealer can't be changed after a job is created</p>
            </div>
          </div>
        } @else {
        <label class="flex flex-col gap-1 relative">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Dealer * <span class="text-xs">({{ allDealers().length }} available)</span></span>
          <input [value]="dealerSearchTerm()" (input)="onDealerSearchInput($any($event.target).value)"
                 (focus)="onDealerFocus()" (blur)="onDealerBlur()"
                 type="text" placeholder="Click to browse or type to search dealers..."
                 class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
          @if (dealerFieldFocused() && !selectedDealer()) {
            <div class="absolute top-full left-0 right-0 mt-1 bg-background dark:bg-surface-container border border-border-subtle dark:border-outline-variant rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto">
              @if (dealersLoading()) {
                <p class="px-3 py-2 text-body-sm text-text-muted dark:text-on-surface-variant">Loading dealers...</p>
              } @else if (dealerSearchResults().length === 0) {
                <p class="px-3 py-2 text-body-sm text-text-muted dark:text-on-surface-variant">No matching dealers.</p>
              } @else {
                @for (dealer of dealerSearchResults(); track dealer.dealerId) {
                  <button type="button" (click)="selectDealer(dealer)"
                          class="w-full text-left px-3 py-2 text-body-sm text-text-main dark:text-on-surface hover:bg-surface-variant dark:hover:bg-surface-variant transition-colors">
                    {{ dealer.dealerName }}
                    <span class="text-text-muted dark:text-on-surface-variant text-xs block">{{ dealer.city }}, {{ dealer.state }}</span>
                  </button>
                }
              }
            </div>
          }
        </label>

        @if (selectedDealer()) {
          <div [formGroup]="dealerForm" class="border border-border-subtle dark:border-outline-variant rounded-lg p-4 flex flex-col gap-3">
            <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Dealer Details <span class="text-xs">(read-only - only Service Advisors can be changed here)</span></span>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label class="flex flex-col gap-1 md:col-span-2">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Dealer Name</span>
                <input formControlName="dealerName" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1 md:col-span-2">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Email</span>
                <input formControlName="email" type="email"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1 md:col-span-2">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Street Name</span>
                <input formControlName="streetName" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">City</span>
                <input formControlName="city" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">State</span>
                <input formControlName="state" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Zip Code</span>
                <input formControlName="zipCode" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Supervisor Name</span>
                <input formControlName="supervisorName" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Phone Number</span>
                <input formControlName="phNo" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Lane Count</span>
                <input formControlName="laneCount" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">CSAT Rating</span>
                <input formControlName="csatRating" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Customer Status</span>
                <input formControlName="customerStatus" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>
              <label class="flex flex-col gap-1">
                <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Traxtion Device</span>
                <input formControlName="traxtionDevice" type="text"
                       class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] disabled:opacity-50 disabled:cursor-not-allowed">
              </label>

              <div class="flex flex-col gap-2 md:col-span-2">
                <div class="flex items-center justify-between">
                  <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Service Advisors <span class="text-xs">(all receive job status emails)</span></span>
                  <button type="button" (click)="addAdvisorLine()" [disabled]="advisorsLoading() || advisorsLoadFailed()"
                          class="text-xs text-[#00B9F2] hover:opacity-80 flex items-center gap-1 disabled:opacity-40">
                    <span class="material-symbols-outlined text-[16px]">add</span> Add Advisor
                  </button>
                </div>
                @if (dealerHasNoEmail()) {
                  <div class="px-3 py-2 rounded-lg bg-[#F29727]/10 border border-[#F29727]/30 flex items-start gap-2">
                    <span class="material-symbols-outlined text-[18px] text-[#F29727] shrink-0">warning</span>
                    <p class="text-xs text-text-main dark:text-on-surface">This dealer has no email on file. Add a service advisor now, or they won't receive job updates or the feedback survey when the job is completed.</p>
                  </div>
                }
                @if (advisorsLoading()) {
                  <p class="text-xs text-text-muted dark:text-on-surface-variant">Loading...</p>
                } @else if (advisorsLoadFailed()) {
                  <p class="text-xs text-error">Couldn't load this dealer's service advisors - reopen the dispatch before editing them, so existing contacts aren't overwritten.</p>
                } @else if (serviceAdvisors().length === 0) {
                  <p class="text-xs text-text-muted dark:text-on-surface-variant">No service advisors added yet.</p>
                }
                @for (advisor of serviceAdvisors(); track $index) {
                  <div class="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.4fr_1fr_auto] gap-2 items-center bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg p-2">
                    <input [value]="advisor.firstName" (input)="updateAdvisorField($index, 'firstName', $any($event.target).value)" placeholder="First name" type="text"
                           class="min-w-0 w-full bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-lg px-2 py-1.5 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
                    <input [value]="advisor.lastName" (input)="updateAdvisorField($index, 'lastName', $any($event.target).value)" placeholder="Last name" type="text"
                           class="min-w-0 w-full bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-lg px-2 py-1.5 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
                    <input [value]="advisor.email" (input)="updateAdvisorField($index, 'email', $any($event.target).value)" placeholder="Email" type="email"
                           class="min-w-0 w-full bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-lg px-2 py-1.5 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
                    <input [value]="advisor.phone" (input)="updateAdvisorField($index, 'phone', $any($event.target).value)" placeholder="Phone" type="text"
                           class="min-w-0 w-full bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-lg px-2 py-1.5 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
                    <button type="button" (click)="removeAdvisorLine($index)" class="p-1 text-text-muted hover:text-[#ef4444] justify-self-end">
                      <span class="material-symbols-outlined text-[18px]">close</span>
                    </button>
                  </div>
                }
              </div>
            </div>
          </div>
        }
        }

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Job Type(s) - a visit can cover more than one</span>
            <div class="border border-border-subtle dark:border-outline-variant rounded-lg max-h-48 overflow-y-auto p-2 flex flex-col gap-2">
              @for (group of jobTypeGroups(); track group.category) {
                <div>
                  <p class="text-xs text-text-muted dark:text-on-surface-variant font-medium px-1 mb-1">{{ group.category }}</p>
                  @for (jobType of group.types; track jobType.jobTypeId) {
                    <label class="flex items-center gap-2 px-1 py-1 rounded hover:bg-surface-variant dark:hover:bg-surface-variant cursor-pointer">
                      <input type="checkbox" [checked]="draftJobTypeIds().includes(jobType.jobTypeId)" (change)="toggleJobType(jobType.jobTypeId)">
                      <span class="text-body-sm text-text-main dark:text-white">{{ jobType.name }} ({{ jobType.durationMinutes }} min)</span>
                    </label>
                  }
                </div>
              }
            </div>
          </div>

          <label class="flex flex-col gap-1">
            <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Technician</span>
            <select [ngModel]="draftTechnicianId()" (ngModelChange)="draftTechnicianId.set($event)"
                    class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
              <option [ngValue]="null">Unassigned</option>
              @for (tech of technicians(); track tech.technicianId) {
                <option [ngValue]="tech.technicianId">{{ tech.firstName }} {{ tech.lastName }}</option>
              }
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="font-label-sm text-text-muted dark:text-on-surface-variant">
              Vehicle
              @if (draftJobTypeIds().length === 0 || !draftScheduledDate()) {
                <span class="text-xs">(set job type + date to check availability)</span>
              }
            </span>
            <select [ngModel]="draftVehicleId()" (ngModelChange)="draftVehicleId.set($event)"
                    class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
              <option [ngValue]="null">None</option>
              @for (vehicle of vehicles(); track vehicle.vehicleId) {
                <option [ngValue]="vehicle.vehicleId" [disabled]="!isVehicleAvailable(vehicle.vehicleId)">
                  {{ vehicle.vehicleName }}{{ isVehicleAvailable(vehicle.vehicleId) ? '' : ' (booked)' }}
                </option>
              }
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Scheduled Date</span>
            <input [ngModel]="draftScheduledDate()" (ngModelChange)="onScheduledDateChange($event)" type="datetime-local"
                   class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Notes</span>
          <textarea [ngModel]="draftNotes()" (ngModelChange)="draftNotes.set($event)" rows="2"
                    class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]"></textarea>
        </label>

        <div class="flex flex-col gap-1">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Parts</span>
          <div class="border border-border-subtle dark:border-outline-variant rounded-lg max-h-48 overflow-y-auto p-2 flex flex-col">
            @for (part of parts(); track part.partId) {
              <div class="flex items-center gap-2 px-1 py-1 rounded hover:bg-surface-variant dark:hover:bg-surface-variant">
                <label class="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                  <input type="checkbox" [checked]="isPartSelected(part.partId)" (change)="togglePart(part.partId)">
                  <span class="text-body-sm text-text-main dark:text-white truncate">{{ part.description }} ({{ part.partNumber }})</span>
                </label>
                @if (isPartSelected(part.partId)) {
                  <input [value]="partQuantity(part.partId)" (input)="updatePartQuantity(part.partId, $any($event.target).value)" type="number" min="1"
                         class="w-16 shrink-0 bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-2 py-1 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
                  <span class="w-16 shrink-0 text-right text-xs text-text-muted dark:text-on-surface-variant">
                    {{ partUnitPrice(part.partId) != null ? ('$' + (partUnitPrice(part.partId)! * partQuantity(part.partId)).toFixed(2)) : '-' }}
                  </span>
                }
              </div>
            }
          </div>
          <div class="flex justify-end pt-2 border-t border-border-subtle dark:border-outline-variant">
            <span class="font-label-md text-text-main dark:text-on-surface">Total: ${{ draftTotal().toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <div class="p-lg border-t border-border-subtle dark:border-outline-variant flex items-center justify-between gap-3">
        @if (error()) {
          <p class="text-error font-body-sm text-body-sm">{{ error() }}</p>
        } @else {
          <span></span>
        }
        <div class="flex items-center gap-3 shrink-0">
          <button type="button" (click)="closeModal()"
                  class="h-[44px] px-6 border border-border-subtle dark:border-outline-variant rounded-lg text-text-main dark:text-on-surface hover:bg-surface-variant dark:hover:bg-surface-variant transition-colors font-label-md">
            Cancel
          </button>
          <button type="button" (click)="saveDispatch()" [disabled]="saving() || (editingJob() === null && !selectedDealer())"
                  class="h-[44px] px-6 bg-[#00B9F2] text-white rounded-lg hover:opacity-90 transition-opacity font-label-md disabled:opacity-50">
            {{ saving() ? 'Saving...' : (editingJob() === null ? 'Create Dispatch' : 'Save Changes') }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

@if (historyModalOpen()) {
  <div class="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" (click)="closeHistoryModal()">
    <div class="bg-background dark:bg-surface-container-low rounded-xl shadow-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" (click)="$event.stopPropagation()">
      <div class="p-lg border-b border-border-subtle dark:border-outline-variant flex items-center justify-between">
        <div>
          <h2 class="font-headline-sm text-headline-sm text-text-main dark:text-on-surface">
            Job #{{ historyJob()?.jobId }} History
          </h2>
          <button type="button" (click)="viewDealerHistoryFromModal()"
                  class="text-text-muted dark:text-on-surface-variant text-xs hover:text-[#00B9F2] hover:underline transition-colors">
            {{ historyJob()?.dealerName }} &middot; view all jobs for this dealer
          </button>
        </div>
        <button (click)="closeHistoryModal()" class="p-1 rounded-full hover:bg-surface-variant dark:hover:bg-surface-variant text-text-muted dark:text-on-surface-variant">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      @if (historyLoading()) {
        <p class="p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm">Loading history...</p>
      } @else if (historyJob(); as job) {
        <div class="p-lg flex flex-col gap-5">
          <div class="flex flex-wrap items-center gap-3">
            <span class="inline-flex items-center px-2 py-1 rounded-full font-label-sm border shrink-0"
                  [style.background-color]="statusColor(job.status) + '1A'"
                  [style.color]="statusColor(job.status)"
                  [style.border-color]="statusColor(job.status) + '33'">
              <span class="w-1.5 h-1.5 rounded-full mr-1.5" [style.background-color]="statusColor(job.status)"></span>
              {{ job.status }}
            </span>
            <span class="text-text-muted dark:text-on-surface-variant text-xs">{{ job.technicianName || 'Unassigned' }}</span>
            @if (job.vehicleName) { <span class="text-text-muted dark:text-on-surface-variant text-xs">&middot; {{ job.vehicleName }}</span> }
            @if (job.durationMinutes) {
              <span class="text-text-muted dark:text-on-surface-variant text-xs">
                &middot; Est. {{ job.durationMinutes }}m @if (job.actualDurationMinutes) { / Actual {{ job.actualDurationMinutes }}m }
              </span>
            }
          </div>

          @if (job.statusHistory && job.statusHistory.length > 0) {
            <div>
              <p class="font-label-sm text-text-muted dark:text-on-surface-variant mb-2">Timeline</p>
              <div class="flex flex-col gap-2">
                @for (entry of job.statusHistory; track entry.changedAt) {
                  <div class="flex items-center gap-2 text-xs">
                    <span class="w-1.5 h-1.5 rounded-full shrink-0" [style.background-color]="statusColor(entry.status)"></span>
                    <span class="font-label-sm" [style.color]="statusColor(entry.status)">{{ entry.status }}</span>
                    <span class="text-text-muted dark:text-on-surface-variant">{{ entry.changedAt | date:'MMM d, y, h:mm a' }}</span>
                  </div>
                }
              </div>
            </div>
          }

          <div>
            <p class="font-label-sm text-text-muted dark:text-on-surface-variant mb-2">Before</p>
            @if (job.beforeNotes) {
              <div class="mb-2">
                <p class="text-text-main dark:text-on-surface text-xs" [class.line-clamp-2]="isLongNotes(job.beforeNotes) && !historyBeforeNotesExpanded()">{{ job.beforeNotes }}</p>
                @if (isLongNotes(job.beforeNotes)) {
                  <button type="button" (click)="historyBeforeNotesExpanded.set(!historyBeforeNotesExpanded())" class="text-xs text-[#00B9F2] hover:opacity-80">
                    {{ historyBeforeNotesExpanded() ? 'Show less' : 'Show more' }}
                  </button>
                }
              </div>
            }
            @if (photosFor('Before').length > 0) {
              <div class="flex flex-wrap gap-2">
                @for (photo of photosFor('Before'); track photo.jobPhotoId) {
                  @if (photo.blobUrl) {
                    <a [href]="photo.blobUrl" target="_blank" class="w-20 h-20 rounded-lg overflow-hidden border border-border-subtle dark:border-outline-variant block">
                      <img [src]="photo.blobUrl" class="w-full h-full object-cover" alt="Before photo" />
                    </a>
                  }
                }
              </div>
            } @else {
              <p class="text-text-muted dark:text-on-surface-variant text-xs">No before photos.</p>
            }
          </div>

          <div>
            <p class="font-label-sm text-text-muted dark:text-on-surface-variant mb-2">After</p>
            @if (job.afterNotes) {
              <div class="mb-2">
                <p class="text-text-main dark:text-on-surface text-xs" [class.line-clamp-2]="isLongNotes(job.afterNotes) && !historyAfterNotesExpanded()">{{ job.afterNotes }}</p>
                @if (isLongNotes(job.afterNotes)) {
                  <button type="button" (click)="historyAfterNotesExpanded.set(!historyAfterNotesExpanded())" class="text-xs text-[#00B9F2] hover:opacity-80">
                    {{ historyAfterNotesExpanded() ? 'Show less' : 'Show more' }}
                  </button>
                }
              </div>
            }
            @if (photosFor('After').length > 0) {
              <div class="flex flex-wrap gap-2">
                @for (photo of photosFor('After'); track photo.jobPhotoId) {
                  @if (photo.blobUrl) {
                    <a [href]="photo.blobUrl" target="_blank" class="w-20 h-20 rounded-lg overflow-hidden border border-border-subtle dark:border-outline-variant block">
                      <img [src]="photo.blobUrl" class="w-full h-full object-cover" alt="After photo" />
                    </a>
                  }
                }
              </div>
            } @else {
              <p class="text-text-muted dark:text-on-surface-variant text-xs">No after photos.</p>
            }
          </div>
        </div>
      }

      <div class="p-lg border-t border-border-subtle dark:border-outline-variant flex items-center justify-between gap-3">
        <button type="button" (click)="closeHistoryModal()"
                class="h-[44px] px-6 border border-border-subtle dark:border-outline-variant rounded-lg text-text-main dark:text-on-surface hover:bg-surface-variant dark:hover:bg-surface-variant transition-colors font-label-md">
          Close
        </button>
        <button type="button" (click)="newDispatchFromHistory()"
                class="h-[44px] px-6 bg-[#00B9F2] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 font-label-md">
          <span class="material-symbols-outlined text-[18px]">add_task</span>
          New Dispatch for This Dealer
        </button>
      </div>
    </div>
  </div>
}
